import React, { useState, useRef, useEffect } from "react";
import {
  Paper,
  Box,
  InputBase,
  IconButton,
  Button,
  Tooltip,
  Popper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import ClearIcon from "@mui/icons-material/Clear";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import HistoryIcon from "@mui/icons-material/History";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";

const SearchHeader = ({
  searchQuery,
  onSearchQueryChange,
  onFilterToggle,
  onSavedSearchesToggle,
  filterPanelOpen,
  savedSearchesPanelOpen,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Ref for search input
  const searchInputRef = useRef(null);

  // State for search suggestions
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // Mock recent searches
  const recentSearches = [
    "QB with 4+ stars",
    "Texas linebackers",
    "Basketball centers over 6'10\"",
    "Running backs with 4.5 or faster 40 time",
    "Florida defensive backs",
  ];

  // Mock popular searches
  const popularSearches = [
    "Top quarterbacks",
    "Defensive linemen",
    "Basketball point guards",
    "High GPA athletes",
    "California recruits",
  ];

  // Handle search input change
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    onSearchQueryChange(value);

    // Show suggestions if input has value
    if (value.trim().length > 0) {
      // Generate suggestions based on input
      // In a real app, this would be an API call
      const mockSuggestions = [
        ...recentSearches.filter((search) =>
          search.toLowerCase().includes(value.toLowerCase())
        ),
        ...popularSearches.filter((search) =>
          search.toLowerCase().includes(value.toLowerCase())
        ),
      ].slice(0, 5);

      setSuggestions(mockSuggestions);
      setShowSuggestions(mockSuggestions.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  // Handle search submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setShowSuggestions(false);

    // Focus out from input
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
  };

  // Handle clear search
  const handleClearSearch = () => {
    onSearchQueryChange("");

    // Focus on input
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    onSearchQueryChange(suggestion);
    setShowSuggestions(false);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        borderRadius: 2,
        background: theme.palette.primary.main,
        color: "white",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          gap: { xs: 2, md: 1.5 },
        }}
      >
        {/* Title */}
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: "bold",
            textAlign: { xs: "center", md: "left" },
            mb: { xs: 0, md: 0 },
            mr: { md: 2 },
          }}
        >
          Advanced Athlete Search
        </Typography>

        {/* Search Form */}
        <Box
          component="form"
          onSubmit={handleSearchSubmit}
          sx={{
            display: "flex",
            flex: { xs: "none", md: 1 },
            position: "relative",
            width: { xs: "100%", md: "auto" },
            order: { xs: 2, md: 1 },
          }}
        >
          <Paper
            sx={{
              p: { xs: "8px 12px", md: "2px 4px" },
              display: "flex",
              alignItems: "center",
              width: "100%",
              borderRadius: 2,
              minHeight: { xs: 48, md: "auto" },
            }}
          >
            <IconButton sx={{ p: "10px" }} aria-label="search">
              <SearchIcon />
            </IconButton>

            <InputBase
              ref={searchInputRef}
              sx={{
                ml: 1,
                flex: 1,
                fontSize: { xs: "16px", md: "14px" }, // Prevent zoom on iOS
              }}
              placeholder={
                isMobile
                  ? "Search athletes..."
                  : "Search athletes, positions, schools..."
              }
              value={searchQuery}
              onChange={handleSearchInputChange}
              onFocus={() =>
                searchQuery &&
                setSuggestions(
                  [...recentSearches, ...popularSearches].slice(0, 5)
                ) &&
                setShowSuggestions(true)
              }
              inputProps={{
                "aria-label": "search athletes",
                style: { fontSize: isMobile ? "16px" : "14px" }, // Prevent zoom on iOS
              }}
            />

            {searchQuery && (
              <IconButton
                sx={{ p: "10px" }}
                aria-label="clear search"
                onClick={handleClearSearch}
              >
                <ClearIcon />
              </IconButton>
            )}
          </Paper>

          {/* Search Suggestions */}
          <Popper
            open={showSuggestions}
            anchorEl={searchInputRef.current}
            placement="bottom-start"
            style={{ width: searchInputRef.current?.offsetWidth, zIndex: 1300 }}
          >
            <Paper
              elevation={3}
              sx={{ mt: 1, maxHeight: 300, overflow: "auto" }}
            >
              <List sx={{ p: 0 }}>
                {suggestions.map((suggestion, index) => (
                  <React.Fragment key={index}>
                    <ListItem
                      button
                      onClick={() => handleSuggestionClick(suggestion)}
                      sx={{
                        py: 1,
                        px: 2,
                        "&:hover": {
                          bgcolor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <HistoryIcon
                        sx={{ mr: 1, color: theme.palette.text.secondary }}
                      />
                      <ListItemText
                        primary={
                          <Typography variant="body2">{suggestion}</Typography>
                        }
                      />
                      <ArrowUpwardIcon
                        fontSize="small"
                        sx={{
                          color: theme.palette.text.secondary,
                          transform: "rotate(45deg)",
                        }}
                      />
                    </ListItem>
                    {index < suggestions.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Popper>
        </Box>

        {/* Action buttons */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "row", md: "row" },
            justifyContent: { xs: "center", md: "flex-end" },
            gap: 1,
            order: { xs: 3, md: 2 },
            width: { xs: "100%", md: "auto" },
            flexShrink: 0,
          }}
        >
          <Tooltip title={filterPanelOpen ? "Hide filters" : "Show filters"}>
            <Button
              variant={filterPanelOpen ? "contained" : "outlined"}
              color="inherit"
              startIcon={<TuneIcon />}
              onClick={onFilterToggle}
              sx={{
                minWidth: { xs: 100, md: 120 },
                fontSize: { xs: "0.875rem", md: "0.875rem" },
                px: { xs: 2, md: 3 },
                bgcolor: filterPanelOpen
                  ? "rgba(255, 255, 255, 0.2)"
                  : "transparent",
                borderColor: "rgba(255, 255, 255, 0.5)",
                "&:hover": {
                  bgcolor: filterPanelOpen
                    ? "rgba(255, 255, 255, 0.3)"
                    : "rgba(255, 255, 255, 0.1)",
                  borderColor: "white",
                },
              }}
            >
              Filters
            </Button>
          </Tooltip>

          {/* Hide saved searches button for now */}
          {false && (
            <Tooltip
              title={
                savedSearchesPanelOpen
                  ? "Hide saved searches"
                  : "Show saved searches"
              }
            >
              <Button
                variant={savedSearchesPanelOpen ? "contained" : "outlined"}
                color="inherit"
                startIcon={<BookmarkIcon />}
                onClick={onSavedSearchesToggle}
                sx={{
                  minWidth: { xs: 100, md: 120 },
                  fontSize: { xs: "0.875rem", md: "0.875rem" },
                  px: { xs: 2, md: 3 },
                  bgcolor: savedSearchesPanelOpen
                    ? "rgba(255, 255, 255, 0.2)"
                    : "transparent",
                  borderColor: "rgba(255, 255, 255, 0.5)",
                  "&:hover": {
                    bgcolor: savedSearchesPanelOpen
                      ? "rgba(255, 255, 255, 0.3)"
                      : "rgba(255, 255, 255, 0.1)",
                    borderColor: "white",
                  },
                }}
              >
                Saved
              </Button>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default SearchHeader;
